import { useEffect, useState, useMemo, useCallback } from 'react';
import { Box, Button, Grid, Typography } from '@mui/material';
import { ServiceItem } from 'orderme-api-integration-client';
import { useTranslation } from 'react-i18next';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { ServiceClient } from '../../helpers/client';
import { roundDoubleNumbers } from '../../helpers/currency';
import { useAppSelector } from '../../redux/hooks';
import { getUserId } from '../../helpers/customerHelper';
import { RootState } from '../../redux/store';
import { ImageLoader } from '../imgLoader/imgLoader';
import { apiKey, imagesUri } from '../../helpers/clientConfigs';
import { CreditsPaymentParam } from '../payment/creditsPayments';
import { LoginDialog } from '../auth/login/loginDialog';
import { DiscountImage, DiscountTypography } from '../serviceLogoItem/serviceLogoItemStyles';

export function BuyCredits() {
  const [creditServices, setCreditServices] = useState<ServiceItem[]>();
  const servicesClient = useMemo(() => new ServiceClient(), []);
  const { t } = useTranslation();
  const routeNavigator = useRouteNavigator();
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const [openLogin, setOpenLogin] = useState(false);

  const fetchCreditServices = useCallback(async () => {
    try {
      let serviceItems = await servicesClient.getAllCreditServices(getUserId());
      setCreditServices(serviceItems?.slice().sort((a, b) => (a.originalPrice ?? 0) - (b.originalPrice ?? 0)));
    } catch (error) {
      console.error('Error fetching credits:', error);
    }
  }, [servicesClient]);

  useEffect(() => {
    fetchCreditServices();
  }, [fetchCreditServices]);

  const openServiceItem = (creditsService: ServiceItem) => {
    routeNavigator.goToCreditsItem(creditsService);
  };

  const buyCredits = (creditService: ServiceItem) => {
    if (!isUserLoggedIn) {
      setOpenLogin(true);
      return;
    }
    routeNavigator.goToCreditsPayments({
      state: {
        id: creditService.id,
        price: creditService.price,
        credits: creditService.originalPrice,
      } as CreditsPaymentParam,
    });
  };

  const closeLogin = () => {
    setOpenLogin(false);
  };

  return (
    <>
      {creditServices?.length > 0 && (
        <Grid
          container
          sx={{
            width: 'auto',
            backgroundColor: 'white',
            borderRadius: '15px',
            color: 'black',
            rowGap: '20px',
            px: '0px',
          }}
        >
          {creditServices.map((item, index) => (
            <Grid sx={{ px: '5px' }} size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
              <Grid container sx={{ flexDirection: 'column', height: '100%' }}>
                <Box
                  sx={{
                    position: 'relative',
                    '&:hover': {
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => openServiceItem(item)}
                >
                  <ImageLoader
                    sx={{
                      width: '100%',
                      height: '200px',
                      objectFit: 'cover',
                      borderRadius: '15px',
                      display: 'block',
                    }}
                    src={item.logoId ? `${imagesUri}/${item.logoId}?orderme-api-key=${apiKey}&height=500` : undefined}
                    alt={item.name}
                  />
                  {item.discount > 0 && (
                    <>
                      <DiscountImage />
                      <DiscountTypography> -{item.discount} %</DiscountTypography>
                    </>
                  )}
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    sx={{
                      '&:hover': {
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => openServiceItem(item)}
                  >
                    {item.name}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Typography
                      sx={{
                        opacity: 0.7,
                        textDecoration: 'line-through',
                        pr: '10px',
                        display: item.originalPrice - item.price === 0 ? 'none' : 'inline-block',
                      }}
                    >
                      {roundDoubleNumbers(item.originalPrice)} €
                    </Typography>
                    <Typography> {roundDoubleNumbers(item.price)} €</Typography>
                  </Box>
                </Box>
                <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                  <Button
                    onClick={() => {
                      buyCredits(item);
                    }}
                    variant="contained"
                  >
                    {t('payment:purchase')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          ))}
          <LoginDialog open={openLogin} onClose={() => closeLogin()} />
        </Grid>
      )}
    </>
  );
}
